import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TriangleAlert } from "lucide-react";
import { resolveOffDayDecision, type OffDayPlanDay } from "@/lib/daycare-hotel/offDayDecision";
import { replaceWeekday, weekdayOfDate } from "@/lib/daycare-hotel/offDayPreview";
import { planDatesAhead } from "@/lib/daycare-hotel/planFixedDays";
import { formatDateBR } from "@/lib/date-utils";
import { cn } from "@/lib/utils";
import type { OffDayResolution } from "./applyOffDayResolution";

const WEEKDAY_SHORT = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

type Option = OffDayResolution["option"];

export interface OffDayPackageOption {
  contractId: string;
  name: string;
  remainingDays: number;
}

export interface OffDayDecisionProps {
  /** `create` nasce da Nova reserva; `move` nasce de remarcar uma diária. */
  mode: "create" | "move";
  targetDate: string;
  petName: string;
  fixedWeekdays: number[];
  planDays: OffDayPlanDay[];
  /** Âncora do ciclo do plano (first_due_on), para a prévia das próximas datas. */
  planAnchor: string;
  /** Quinzenal: 2. Com `planStartsOn`, marca as semanas de folga. */
  weekInterval?: number;
  planStartsOn?: string;
  /** Remarcar: data da diária que está saindo. */
  movingDate?: string;
  /** Pacotes do pet com saldo: a diária extra pode sair deles. */
  packages?: OffDayPackageOption[];
  adHocPriceLabel?: string | null;
  value: OffDayResolution | null;
  onChange: (next: OffDayResolution | null) => void;
}

const cardClass = (checked: boolean) =>
  cn(
    "w-full rounded-xl border px-3 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    checked ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-muted",
  );

const chipClass = (checked: boolean) =>
  cn(
    "rounded-full border px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    checked ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-foreground hover:bg-muted",
  );

/**
 * Decisão para um dia fora do plano, dentro do próprio formulário. Cada opção
 * mostra o antes → depois com datas reais, para ninguém escolher às cegas.
 */
export function OffDayDecision({
  mode,
  targetDate,
  petName,
  fixedWeekdays,
  planDays,
  planAnchor,
  weekInterval = 1,
  planStartsOn,
  movingDate,
  packages = [],
  adHocPriceLabel = null,
  onChange,
}: OffDayDecisionProps) {
  const { t } = useTranslation("daycareHotel");
  const decision = useMemo(
    () => resolveOffDayDecision({ targetDate, fixedWeekdays, planDays, weekInterval, startsOn: planStartsOn }),
    [targetDate, fixedWeekdays, planDays, weekInterval, planStartsOn],
  );
  const [option, setOption] = useState<Option | null>(null);
  const [dayIdOut, setDayIdOut] = useState<string | null>(null);
  const [weekdayOut, setWeekdayOut] = useState<number | null>(null);
  const [adHocChoice, setAdHocChoice] = useState<string | null>(null);

  if (!decision.isOffDay) return null;

  const weekdayLabel = (weekday: number) => t(`schedule.weekdayShort.${WEEKDAY_SHORT[weekday]}`);
  const daysLabel = (weekdays: number[]) => weekdays.map(weekdayLabel).join(" · ");
  const dayLabel = (date: string) => `${weekdayLabel(weekdayOfDate(date))} ${formatDateBR(date).slice(0, 5)}`;
  const targetWeekday = weekdayOfDate(targetDate);
  const movingWeekday = movingDate ? weekdayOfDate(movingDate) : null;

  // Na semana de folga do quinzenal mudar os dias não resolve: remarcar só move,
  // e a reserva nova só pode ser diária extra.
  const options: Option[] = mode === "move"
    ? decision.isOffWeek ? ["move_once"] : ["move_once", "change_plan"]
    : [...(decision.options.includes("swap_week") ? (["swap_week"] as const) : []), ...decision.options.filter((value) => value !== "swap_week")];

  const selectOption = (next: Option) => {
    setOption(next);
    setDayIdOut(null);
    setAdHocChoice(null);
    if (next === "move_once") return onChange({ option: "move_once" });
    if (next === "change_plan" && mode === "move" && movingWeekday !== null) {
      setWeekdayOut(movingWeekday);
      return onChange({ option: "change_plan", weekdayOut: movingWeekday });
    }
    setWeekdayOut(null);
    if (next === "ad_hoc" && packages.length === 0) {
      setAdHocChoice("charge");
      return onChange({ option: "ad_hoc", packageContractId: null });
    }
    onChange(null);
  };

  const renderDetails = (value: Option): ReactNode => {
    if (value === "swap_week") {
      const out = decision.swappableDays.find((day) => day.id === dayIdOut);
      return (
        <div className="grid gap-2">
          <span className="text-xs text-muted-foreground">{t("offDay.swapPick")}</span>
          <div role="radiogroup" aria-label={t("offDay.swapPick")} className="flex flex-wrap gap-1.5">
            {decision.swappableDays.map((day) => (
              <button
                key={day.id}
                type="button"
                role="radio"
                aria-checked={dayIdOut === day.id}
                onClick={() => {
                  setDayIdOut(day.id);
                  onChange({ option: "swap_week", dayIdOut: day.id });
                }}
                className={chipClass(dayIdOut === day.id)}
              >
                {dayLabel(day.date)}
              </button>
            ))}
          </div>
          {out ? <p className="text-sm text-foreground">{t("offDay.swapPreview", { out: dayLabel(out.date), in: dayLabel(targetDate) })}</p> : null}
        </div>
      );
    }

    if (value === "change_plan") {
      const after = weekdayOut !== null ? replaceWeekday(fixedWeekdays, weekdayOut, targetWeekday) : null;
      const next = after
        ? planDatesAhead({ anchor: planAnchor, from: targetDate, weekdays: after, weekInterval, startsOn: planStartsOn }).slice(0, 3).map(dayLabel).join(" · ")
        : "";
      return (
        <div className="grid gap-2">
          {mode === "create" ? (
            <>
              <span className="text-xs text-muted-foreground">{t("offDay.changePick")}</span>
              <div role="radiogroup" aria-label={t("offDay.changePick")} className="flex flex-wrap gap-1.5">
                {decision.replaceableWeekdays.map((weekday) => (
                  <button
                    key={weekday}
                    type="button"
                    role="radio"
                    aria-checked={weekdayOut === weekday}
                    aria-label={weekdayLabel(weekday)}
                    onClick={() => {
                      setWeekdayOut(weekday);
                      onChange({ option: "change_plan", weekdayOut: weekday });
                    }}
                    className={chipClass(weekdayOut === weekday)}
                  >
                    {weekdayLabel(weekday)}
                  </button>
                ))}
              </div>
            </>
          ) : null}
          {after ? (
            <>
              <p className="text-sm text-foreground">{t("offDay.changePreview", { before: daysLabel(fixedWeekdays), after: daysLabel(after) })}</p>
              {next ? <p className="text-xs text-muted-foreground">{t("offDay.nextDates", { dates: next })}</p> : null}
            </>
          ) : null}
        </div>
      );
    }

    if (value === "ad_hoc") {
      const chargeLabel = adHocPriceLabel
        ? t("offDay.adHocCharge", { price: adHocPriceLabel })
        : t("offDay.adHocChargeNoPrice");
      if (packages.length === 0) return <p className="text-sm text-foreground">{chargeLabel}</p>;
      return (
        <div role="radiogroup" aria-label={t("offDay.options.ad_hoc")} className="grid gap-1.5">
          {packages.map((pack) => (
            <button
              key={pack.contractId}
              type="button"
              role="radio"
              aria-checked={adHocChoice === pack.contractId}
              onClick={() => {
                setAdHocChoice(pack.contractId);
                onChange({ option: "ad_hoc", packageContractId: pack.contractId });
              }}
              className={chipClass(adHocChoice === pack.contractId)}
            >
              {t("offDay.adHocPackage", { name: pack.name, count: pack.remainingDays })}
            </button>
          ))}
          <button
            type="button"
            role="radio"
            aria-checked={adHocChoice === "charge"}
            onClick={() => {
              setAdHocChoice("charge");
              onChange({ option: "ad_hoc", packageContractId: null });
            }}
            className={chipClass(adHocChoice === "charge")}
          >
            {chargeLabel}
          </button>
        </div>
      );
    }

    return movingDate ? (
      <p className="text-sm text-foreground">{t("offDay.moveOncePreview", { from: dayLabel(movingDate), to: dayLabel(targetDate) })}</p>
    ) : null;
  };

  return (
    <section className="grid gap-3 rounded-xl border border-state-warning/40 bg-state-warning-soft p-4">
      <p className="flex items-start gap-2 text-sm font-semibold text-foreground">
        <TriangleAlert aria-hidden="true" size={16} className="mt-0.5 shrink-0 text-state-warning" />
        {decision.isOffWeek
          ? t("offDay.titleOffWeek", { day: dayLabel(targetDate), pet: petName })
          : t("offDay.title", { day: dayLabel(targetDate), pet: petName, days: daysLabel(fixedWeekdays) })}
      </p>
      <div role="radiogroup" aria-label={t("offDay.optionsLabel")} className="grid gap-2">
        {options.map((value) => (
          <div key={value} className="grid gap-2">
            <button
              type="button"
              role="radio"
              aria-checked={option === value}
              aria-label={t(`offDay.options.${value}`)}
              aria-describedby={`off-day-hint-${value}`}
              onClick={() => selectOption(value)}
              className={cardClass(option === value)}
            >
              <span className="block text-sm font-medium text-foreground">{t(`offDay.options.${value}`)}</span>
              <span id={`off-day-hint-${value}`} className="block text-xs text-muted-foreground">
                {t(`offDay.hints.${value}`)}
              </span>
            </button>
            {option === value ? <div className="pl-3">{renderDetails(value)}</div> : null}
          </div>
        ))}
      </div>
    </section>
  );
}
